import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Download,
  Eye,
  MoreVertical,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Database,
  ShieldCheck,
  FileText,
  Cpu,
  Package,
  Settings,
  Code2,
  Award,
  ArrowUp,
  Plus,
  CheckCircle2,
  Layers,
  Box,
} from 'lucide-react';

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const UPLOAD_FILE_ROUTE = '/blockchain-anchoring/ipfs-files/upload';

const TYPE_STYLES = {
  PDF: { icon: FileText, cls: 'bg-rose-50 text-rose-600' },
  Binary: { icon: Cpu, cls: 'bg-sky-50 text-sky-600' },
  ZIP: { icon: Package, cls: 'bg-violet-50 text-violet-600' },
  YAML: { icon: Settings, cls: 'bg-emerald-50 text-emerald-600' },
  TAR: { icon: Code2, cls: 'bg-orange-50 text-orange-600' },
  CERT: { icon: Award, cls: 'bg-amber-50 text-amber-600' },
};

const summaryCards = [
  { title: 'Total Files', value: '186', change: '18% vs. last month', Icon: FileText, neutral: false },
  { title: 'Stored on IPFS', value: '172', change: '21% vs. last month', Icon: Database, neutral: false },
  { title: 'Verified on Blockchain', value: '186', change: '18% vs. last month', Icon: Layers, neutral: false },
  { title: 'Integrity Verified', value: '100%', change: 'No change vs. last month', Icon: ShieldCheck, neutral: true },
];

const FILE_TYPES = ['All File Types', 'PDF', 'Binary', 'ZIP', 'YAML', 'TAR', 'CERT'];
const ORGANIZATIONS = ['All Organizations', 'Acme Corp', 'Global Logistics', 'CyberSec Inc'];
const PAGE_SIZE = 10;

const baseFiles = [
  {
    id: '1', fileName: 'contract_agreement.pdf', filePath: '/contracts/', type: 'PDF',
    cidShort: 'QmXn7e9g8JbW2n1f...', cidFull: 'QmXn7e9g8JbW2n1f4Zk9v6b7Q3h5T8a1pZL4m6NBo09pQr',
    txIdShort: '0x7f3a9c4e2b6d...', txIdFull: '0x7f3a9c4e2b6d8e9f4a1c2b3d4e5f6a7b8c9d0e1f',
    size: '1.2 MB', uploadedDate: 'May 31, 2025', uploadedTime: '10:42 AM',
    uploadedBy: 'Super Admin', organization: 'Acme Corp', blockNumber: '7,842,551',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '2', fileName: 'firmware_v2.1.bin', filePath: '/firmware/', type: 'Binary',
    cidShort: 'QmTF8v3h9kL2p6z1...', cidFull: 'QmTF8v3h9kL2p6z1Rm4Xc8Yw3Nq5Ub7Ea9Dj2Gs6Hf1Kt',
    txIdShort: '0x9a2b4c6d7e8f...', txIdFull: '0x9a2b4c6d7e8f1a2b3c4d5e6f7a8b9c0d1e2f3a4b',
    size: '8.8 MB', uploadedDate: 'May 30, 2025', uploadedTime: '04:15 PM',
    uploadedBy: 'DevOps Bot', organization: 'Acme Corp', blockNumber: '7,841,200',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '3', fileName: 'deployment_package.zip', filePath: '/deployments/', type: 'ZIP',
    cidShort: 'QmZ8k6p9w1n4t7e2...', cidFull: 'QmZ8k6p9w1n4t7e2Qa5Sd8Fg3Hj6Kl9Zx2Cv5Bn8Mq1Wr',
    txIdShort: '0x5c7e2d9f1b3a...', txIdFull: '0x5c7e2d9f1b3a4c6d5e6f7a8b9c0d1e2f3a4b5c6d',
    size: '15.6 MB', uploadedDate: 'May 30, 2025', uploadedTime: '04:32 PM',
    uploadedBy: 'Release Manager', organization: 'Acme Corp', blockNumber: '7,841,310',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '4', fileName: 'k8s_manifest.yaml', filePath: '/k8s/', type: 'YAML',
    cidShort: 'QmN1z4g8t3h9e6w2...', cidFull: 'QmN1z4g8t3h9e6w2Pk5Lm8Jn3Hb6Vc9Xz2Aq5Sw8Ed1Rf',
    txIdShort: '0x3d8f61a1c2e7...', txIdFull: '0x3d8f61a1c2e7b9f0a1b2c3d4e5f6a7b8c9d0e1f2',
    size: '12 KB', uploadedDate: 'May 28, 2025', uploadedTime: '03:21 PM',
    uploadedBy: 'K8s Operator', organization: 'Acme Corp', blockNumber: '7,830,450',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '5', fileName: 'report_final.pdf', filePath: '/reports/', type: 'PDF',
    cidShort: 'QmR7t3n9p5v1k2o6...', cidFull: 'QmR7t3n9p5v1k2o6Fg2Hj5Kl8Zx1Cv4Bn7Mq9Ws3Ed6Rt2',
    txIdShort: '0x8e4c7d2a9f1b...', txIdFull: '0x8e4c7d2a9f1b3c5e7a9b0c1d2e3f4a5b6c7d8e9f',
    size: '2.4 MB', uploadedDate: 'May 27, 2025', uploadedTime: '09:12 AM',
    uploadedBy: 'Audit Lead', organization: 'Acme Corp', blockNumber: '7,824,110',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '6', fileName: 'iot_device_firmware.bin', filePath: '/iot/', type: 'Binary',
    cidShort: 'QmF6p1t8v9z3k2m7...', cidFull: 'QmF6p1t8v9z3k2m7Ns2Bv5Cx8Zl1Km4Jh7Gf9Ds3Aq6Wr',
    txIdShort: '0x6b2f9e8d7c1a...', txIdFull: '0x6b2f9e8d7c1a4f3e5a6b7c8d9e0f1a2b3c4d5e6f',
    size: '4.3 MB', uploadedDate: 'May 26, 2025', uploadedTime: '02:45 PM',
    uploadedBy: 'IoT Team', organization: 'Acme Corp', blockNumber: '7,818,900',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '7', fileName: 'source_code.tar.gz', filePath: '/code/', type: 'TAR',
    cidShort: 'QmP9k2v7n5t4e8z1...', cidFull: 'QmP9k2v7n5t4e8z1Hd3Fg6Jk9Lq2Zx5Cv8Bn1Mw4Er7Ty',
    txIdShort: '0x4d7a9c3e2b6f...', txIdFull: '0x4d7a9c3e2b6f1a8c2b3d4e5f6a7b8c9d0e1f2a3b',
    size: '22.7 MB', uploadedDate: 'May 25, 2025', uploadedTime: '11:30 AM',
    uploadedBy: 'Lead Dev', organization: 'Acme Corp', blockNumber: '7,812,400',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '8', fileName: 'policy_document.pdf', filePath: '/policies/', type: 'PDF',
    cidShort: 'QmT5r8p3k1n6v9e2...', cidFull: 'QmT5r8p3k1n6v9e2Pd7Fs3Gh9Jk2Lz5Xc8Vb1Nm4Qw6Er',
    txIdShort: '0x9f1c6e2d4a7b...', txIdFull: '0x9f1c6e2d4a7b3e2f1a2b3c4d5e6f7a8b9c0d1e2f',
    size: '980 KB', uploadedDate: 'May 24, 2025', uploadedTime: '04:10 PM',
    uploadedBy: 'Compliance Officer', organization: 'Acme Corp', blockNumber: '7,805,120',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '9', fileName: 'certificate.crt', filePath: '/certificates/', type: 'CERT',
    cidShort: 'QmV6n3t9p4k8z1e2...', cidFull: 'QmV6n3t9p4k8z1e2Rd4Fg7Hj9Kl3Zx6Cv1Bn5Mq8Ws2Er',
    txIdShort: '0x7c3e9a1d5f2b...', txIdFull: '0x7c3e9a1d5f2b4c8e1a2b3c4d5e6f7a8b9c0d1e2f',
    size: '5 KB', uploadedDate: 'May 25, 2025', uploadedTime: '01:25 PM',
    uploadedBy: 'Security Admin', organization: 'Acme Corp', blockNumber: '7,813,000',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
  {
    id: '10', fileName: 'application_build.zip', filePath: '/builds/', type: 'ZIP',
    cidShort: 'QmJ8p2v6t9n4k3e1...', cidFull: 'QmJ8p2v6t9n4k3e1Sd5Fg8Hj2Kl6Zx9Cv3Bn7Mq1Wr4Et',
    txIdShort: '0x5e9b3c7a1d2f...', txIdFull: '0x5e9b3c7a1d2f8f4a1a2b3c4d5e6f7a8b9c0d1e2f',
    size: '18.4 MB', uploadedDate: 'May 24, 2025', uploadedTime: '10:18 AM',
    uploadedBy: 'CI/CD Pipeline', organization: 'Acme Corp', blockNumber: '7,802,890',
    network: 'Ethereum (Public)', status: 'Verified',
    integrityCheck: 'Passed', blockchainMatch: 'Matched', fileTampering: 'Not Detected',
  },
];

export default function IPFSFiles() {
  const navigate = useNavigate();

  const [activeTab] = useState('ipfs-files');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All File Types');
  const [orgFilter, setOrgFilter] = useState('All Organizations');
  const [selectedFile, setSelectedFile] = useState(baseFiles[0]);
  const [page, setPage] = useState(1);
  const [selectedSideTab, setSelectedSideTab] = useState('Overview');

  const filteredFiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    return baseFiles.filter((file) => {
      const matchesSearch =
        !query ||
        file.fileName.toLowerCase().includes(query) ||
        file.id.toLowerCase().includes(query) ||
        file.cidFull.toLowerCase().includes(query) ||
        file.txIdFull.toLowerCase().includes(query);
      const matchesType = typeFilter === 'All File Types' || file.type === typeFilter;
      const matchesOrg = orgFilter === 'All Organizations' || file.organization === orgFilter;
      return matchesSearch && matchesType && matchesOrg;
    });
  }, [search, typeFilter, orgFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredFiles.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleFiles = filteredFiles.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstFile = filteredFiles.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastFile = Math.min(currentPage * PAGE_SIZE, filteredFiles.length);
  const pageButtons = totalPages > 1 ? [1, 2, 3, 4, 5, '...', totalPages] : [1];

  const updateFilter = (setter, value) => { setter(value); setPage(1); };
  const handlePageChange = (nextPage) => setPage(Math.max(1, Math.min(nextPage, totalPages)));

  return (
    <div className="min-h-screen bg-[#f7fbff] text-[#1e3559]">
      {/* Top tabs */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;
              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-900 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={() => navigate(UPLOAD_FILE_ROUTE)}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-[#6200ee] px-3 text-[10px] font-medium text-white shadow-sm transition hover:bg-[#5100c9]"
          >
            <Plus className="h-3.5 w-3.5" />
            Upload File
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        {/* Summary cards */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.Icon;
            return (
              <div
                key={card.title}
                className="min-w-0 rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                    <Icon className="h-7 w-7 text-[#6426df]" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">{card.title}</p>
                    <p className="text-xl font-bold leading-tight text-[#20375e]">{card.value}</p>
                    <p className={`text-[9px] leading-tight ${card.neutral ? 'text-[#7b91b0]' : 'text-[#21a675]'}`}>
                      {!card.neutral && <ArrowUp className="h-3 w-3" strokeWidth={2} />}
                      {card.change}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Table + right details sidebar */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_240px] xl:grid-cols-[minmax(0,1fr)_255px]">
          {/* Table card */}
          <div className="min-w-0 overflow-hidden rounded-md border border-[#e5edf7] bg-white shadow-sm">

            {/* Filters bar */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-[#edf2f8] bg-[#f8fbff] px-3 py-2">
              <div className="relative min-w-[160px] flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[#86a0be]" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => updateFilter(setSearch, e.target.value)}
                  placeholder="Search by file name, hash, or CID..."
                  className="h-6 w-full rounded border border-[#dfe9f4] bg-white pl-6 pr-2 text-[10px] text-[#49678f] outline-none placeholder:text-[#91a5bf] focus:border-[#8d62ea]"
                />
              </div>
              <div className="relative">
                <select value={typeFilter} onChange={(e) => updateFilter(setTypeFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {FILE_TYPES.map((type) => <option key={type}>{type}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <div className="relative">
                <select value={orgFilter} onChange={(e) => updateFilter(setOrgFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {ORGANIZATIONS.map((org) => <option key={org}>{org}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] text-[#5b769c] hover:bg-white">
                <Calendar className="h-3 w-3 text-[#718daf]" />
                May 1 – May 31, 2025
                <ChevronDown className="h-2.5 w-2.5 text-[#87a0be]" />
              </button>
              <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] font-medium text-[#6426df] hover:bg-violet-50">
                <Download className="h-3 w-3" />
                Export
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-[950px] w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-[42px]" />
                  <col className="w-[170px]" />
                  <col className="w-[90px]" />
                  <col className="w-[140px]" />
                  <col className="w-[140px]" />
                  <col className="w-[110px]" />
                  <col className="w-[75px]" />
                  <col className="w-[80px]" />
                  <col className="w-[75px]" />
                </colgroup>
                <thead>
                  <tr className="h-9 border-b border-[#edf2f8] bg-[#f8fbff] text-left text-[10px] font-semibold uppercase tracking-wide text-[#7791b3]">
                    <th className="w-[42px] min-w-[42px] px-3 text-center">#</th>
                    <th className="px-2">File Name</th>
                    <th className="px-2">Type</th>
                    <th className="px-2">CID (IPFS)</th>
                    <th className="px-2">Blockchain TX ID</th>
                    <th className="px-2">Uploaded On</th>
                    <th className="px-2">Size</th>
                    <th className="px-2">Status</th>
                    <th className="w-[75px] min-w-[75px] px-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFiles.map((file, index) => {
                    const typeStyle = TYPE_STYLES[file.type] || TYPE_STYLES.PDF;
                    const TypeIcon = typeStyle.icon;
                    const isSelected = selectedFile?.id === file.id;
                    return (
                      <tr
                        key={file.id}
                        onClick={() => setSelectedFile(file)}
                        className={`h-[49px] cursor-pointer border-b border-[#f0f4f8] transition hover:bg-[#f9fbff] ${isSelected ? 'bg-[#fbfaff]' : 'bg-white'}`}
                      >
                        <td className="w-[42px] min-w-[42px] px-3 text-center text-xs font-medium text-[#58749c]">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td className="px-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${typeStyle.cls}`}>
                              <TypeIcon className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-medium text-[#46658e]" title={file.fileName}>{file.fileName}</p>
                              <p className="truncate text-[10px] text-[#8098b6]" title={file.filePath}>{file.filePath}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2">
                          <span className={`inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${typeStyle.cls}`}>
                            <TypeIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{file.type}</span>
                          </span>
                        </td>
                        <td className="px-2">
                          <p title={file.cidFull} className="truncate font-mono text-[11px] text-[#6f88a8]">{file.cidShort}</p>
                        </td>
                        <td className="px-2">
                          <p title={file.txIdFull} className="truncate font-mono text-[11px] text-[#6f88a8]">{file.txIdShort}</p>
                        </td>
                        <td className="px-2">
                          <p className="text-xs leading-4 text-[#56729a]">{file.uploadedDate}</p>
                          <p className="text-[11px] leading-4 text-[#8098b6]">{file.uploadedTime}</p>
                        </td>
                        <td className="px-2">
                          <p className="text-xs text-[#56729a]">{file.size}</p>
                        </td>
                        <td className="px-2">
                          <StatusBadge status={file.status} />
                        </td>
                        <td className="w-[75px] min-w-[75px] px-2">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedFile(file); }}
                              title="View file details"
                              className="text-[#7c9fca] transition hover:text-[#6426df]"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              title="More actions"
                              className="text-[#8ca4c2] transition hover:text-[#6426df]"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleFiles.length === 0 && (
                    <tr>
                      <td colSpan={9} className="h-72 text-center text-xs text-[#8ca2bd]">No files found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-t border-[#edf2f8] px-3 py-3">
              <p className="text-[9px] text-[#6682aa]">
                Showing {firstFile}-{lastFile} of {filteredFiles.length} files
              </p>
              <nav aria-label="IPFS files pagination" className="flex items-center gap-1">
                <PaginationButton disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </PaginationButton>
                {pageButtons.map((item) =>
                  item === '...' ? (
                    <span key="ellipsis" className="flex h-7 w-7 items-center justify-center rounded border border-[#e2eaf4] text-[9px] text-[#7690b2]">...</span>
                  ) : (
                    <PaginationButton key={item} active={currentPage === item} onClick={() => handlePageChange(item)} label={`Page ${item}`}>
                      {item}
                    </PaginationButton>
                  )
                )}
                <PaginationButton disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </PaginationButton>
              </nav>
            </div>
          </div>

          {/* Right-side file details */}
          {selectedFile && (
            <FileDetails
              file={selectedFile}
              selectedSideTab={selectedSideTab}
              setSelectedSideTab={setSelectedSideTab}
              onClose={() => setSelectedFile(null)}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function PaginationButton({ children, onClick, disabled = false, active = false, label }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex h-7 w-7 items-center justify-center rounded border text-[9px] font-medium transition ${
        active
          ? 'border-[#6200ee] bg-[#6200ee] text-white'
          : 'border-[#e2eaf4] bg-white text-[#718caf] hover:bg-[#f7faff]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Verified: 'bg-[#dff7ea] text-[#29996e]',
    Anchored: 'bg-[#dff7ea] text-[#29996e]',
    Pending: 'bg-amber-50 text-amber-600',
    Failed: 'bg-rose-50 text-rose-600',
  };
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[8px] font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

function DetailsSection({ title, Icon, children }) {
  return (
    <section className="border-t border-[#eaf0f7] pt-3">
      <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-[#29446c]">
        <Icon className="h-3.5 w-3.5 text-[#6426df]" />
        {title}
      </h3>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function DetailRow({ label, value, children, link = false, compact = false }) {
  return (
    <div className="grid grid-cols-[90px_minmax(0,1fr)] items-start gap-2">
      <span className="text-[11px] leading-[15px] text-[#8198b7]">{label}</span>
      {children ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="flex min-w-0 items-start gap-1">
          <span
            title={value}
            className={`min-w-0 break-all leading-[15px] ${compact ? 'font-mono text-[10px]' : 'text-[11px]'} ${link ? 'text-[#6426df]' : 'text-[#55719a]'}`}
          >
            {value}
          </span>
          {link && <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[#6426df]" />}
        </div>
      )}
    </div>
  );
}

function StatusCheckRow({ label, status }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-[#8198b7]">{label}</span>
      <span className="inline-flex items-center gap-1 rounded bg-[#dff7ea] px-1.5 py-0.5 text-[10px] font-medium text-[#29996e]">
        <CheckCircle2 className="h-2.5 w-2.5" />
        {status}
      </span>
    </div>
  );
}

function FileDetails({ file, selectedSideTab, setSelectedSideTab, onClose }) {
  const typeStyle = TYPE_STYLES[file.type] || TYPE_STYLES.PDF;
  const FileTypeIcon = typeStyle.icon;
  const ipfsGatewayUrl = `https://ipfs.io/ipfs/${file.cidFull}`;

  return (
    <aside className="min-w-0 rounded-md border border-[#e5edf7] bg-white p-3 shadow-sm">
      {/* Header */}
      <div className="mb-2.5 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold text-[#29446c]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#2ab98c]" />
          File Details
        </h2>
        <StatusBadge status={file.status} />
      </div>

      {/* File identity */}
      <div className="mb-3 flex items-center gap-2">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${typeStyle.cls}`}>
          <FileTypeIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h3 title={file.fileName} className="truncate text-xs font-semibold text-[#29446c]">{file.fileName}</h3>
          <p className="mt-0.5 text-[11px] text-[#8299b8]">{file.type} &nbsp;•&nbsp; {file.size}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="mb-3 flex border-b border-[#eaf0f7]">
        {['Overview', 'IPFS Details', 'Blockchain'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setSelectedSideTab(tab)}
            className={`relative flex-1 pb-2 text-center text-[10px] font-medium transition ${
              selectedSideTab === tab ? 'text-[#6426df]' : 'text-[#8198b7] hover:text-[#29446c]'
            }`}
          >
            {tab}
            {selectedSideTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#6426df]" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {selectedSideTab === 'Overview' && (
        <div className="space-y-2.5">
          <DetailRow label="File Name" value={file.fileName} />
          <DetailRow label="File Type" value={file.type} />
          <DetailRow label="Size" value={file.size} />
          <DetailRow label="Uploaded By" value={file.uploadedBy} />
          <DetailRow label="Uploaded On" value={`${file.uploadedDate} ${file.uploadedTime}`} />
          <DetailRow label="Organization" value={file.organization} />

          <DetailsSection title="IPFS" Icon={Box}>
            <DetailRow label="CID" value={file.cidFull} link compact />
            <DetailRow label="Gateway" value={ipfsGatewayUrl} link compact />
            <DetailRow label="Explorer">
              <button type="button" className="inline-flex items-center gap-1 text-[11px] text-[#6426df] hover:underline">
                View on IPFS <ExternalLink className="h-2.5 w-2.5" />
              </button>
            </DetailRow>
          </DetailsSection>

          <DetailsSection title="Blockchain" Icon={ShieldCheck}>
            <DetailRow label="TX ID" value={file.txIdFull} link compact />
            <DetailRow label="Network" value={file.network} />
            <DetailRow label="Block No." value={file.blockNumber} />
            <DetailRow label="Explorer">
              <button type="button" className="inline-flex items-center gap-1 text-[11px] text-[#6426df] hover:underline">
                View on Etherscan <ExternalLink className="h-2.5 w-2.5" />
              </button>
            </DetailRow>
          </DetailsSection>

          <DetailsSection title="Verification" Icon={ShieldCheck}>
            <StatusCheckRow label="Integrity Check" status={file.integrityCheck} />
            <StatusCheckRow label="Blockchain Match" status={file.blockchainMatch} />
            <StatusCheckRow label="File Tampering" status={file.fileTampering} />
          </DetailsSection>
        </div>
      )}

      {selectedSideTab === 'IPFS Details' && (
        <div className="space-y-2.5">
          <DetailRow label="Network" value="IPFS Public Network" />
          <DetailRow label="Storage Node" value="ipfs-node-01" />
          <DetailRow label="Replication" value="3 replicas" />
          <DetailRow label="Pin Status" value="Pinned" />
          <DetailRow label="Availability" value="100%" />
          <DetailRow label="Full CID" value={file.cidFull} compact />
        </div>
      )}

      {selectedSideTab === 'Blockchain' && (
        <div className="space-y-2.5">
          <DetailRow label="Consensus" value="Proof of Stake" />
          <DetailRow label="Gas Used" value="21,450 gas" />
          <DetailRow label="Contract" value="0x1A2b...3C4d" compact />
          <DetailRow label="Timestamp" value={`${file.uploadedDate} ${file.uploadedTime}`} />
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="flex flex-1 h-7 items-center justify-center gap-1.5 rounded border border-[#a987ed] bg-white text-xs font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          <Download className="h-3 w-3" />
          Download
        </button>
        <button
          type="button"
          className="flex flex-1 h-7 items-center justify-center gap-1.5 rounded border border-[#a987ed] bg-white text-xs font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          <ExternalLink className="h-3 w-3" />
          View Cert
        </button>
      </div>
    </aside>
  );
}